
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { api } from '../services/api';
import type { Notification } from '../types';
import { useAuth } from '../hooks/useAuth';

import { devError } from '@/utils/devLog';
interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (user) {
      try {
        setLoading(true);
        const data = await api.getNotificationsForUser(user.id, { limit: 10 });
        setNotifications(data);
      } catch (error) {
        devError('Failed to fetch notifications', error);
      } finally {
        setLoading(false);
      }
    } else {
        setNotifications([]);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const unreadCount = notifications.filter(n => !n.lu_a).length;

  const markAsRead = async (notificationId: string) => {
    const notif = notifications.find(n => n.id === notificationId);
    if (notif && !notif.lu_a) {
        setNotifications(prev => 
            prev.map(n => n.id === notificationId ? { ...n, lu_a: new Date().toISOString() } : n)
        );
        try {
            await api.markNotificationAsRead(notificationId);
        } catch (error) {
            devError('Failed to mark notification as read', error);
            fetchNotifications();
        }
    }
  };

  const markAllAsRead = async () => {
    if(user){
        setNotifications(prev => prev.map(n => ({...n, lu_a: new Date().toISOString() })));
        try {
            await api.markAllNotificationsAsRead(user.id);
        } catch (error) {
            devError('Failed to mark all notifications as read', error);
            fetchNotifications();
        }
    }
  };

  const deleteNotification = async (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    try {
      await api.deleteNotification(notificationId);
    } catch (error) {
      devError('Failed to delete notification', error);
      fetchNotifications();
    }
  };

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
